import { hasStatusCode, isAPIError, useGetReportQuery } from "@app.raytd.com/store";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { ReportEntity, ReportRevision, PublishedReport, ReportPermissions } from "store/src/lib/services/types";
import ErrorCard from "../components/error-card";
import { useLocation, useNavigate } from "react-router-dom";
import { SelectedFilters } from "../types/filters";
import { defaultInitialFilters } from "../utils/useReportItemsFilter";
import { useForbiddenFallback } from "@/components/raytd/guards/use-forbidden-fallback";

export interface BaseReportContext {
    report: ReportEntity | null;
    isLoading: boolean;
    error: unknown;
    isEditable: boolean;
    editMode: boolean;
    setEditMode: (editMode: boolean) => void;
    revisionId: string | null;
    isPublished: boolean;
    publishedReportData?: {
        published: PublishedReport;
        revision: ReportRevision;
    } | null;
    permissions?: ReportPermissions;
    revision: ReportRevision | null;
    viewFiltersMap: { [viewId: string]: SelectedFilters };
    setViewFilters: (viewId: string, filters: SelectedFilters | ((prev: SelectedFilters) => SelectedFilters)) => void;
}

export const ReportContext = createContext<BaseReportContext>({
    report: null,
    isLoading: false,
    error: null,
    isEditable: false,
    editMode: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setEditMode: () => { },
    revisionId: null,
    isPublished: false,
    publishedReportData: null,
    permissions: null,
    revision: null,
    viewFiltersMap: {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setViewFilters: () => { },
});

interface ReportProviderProps {
    reportId: string;
    children: React.ReactNode;
    initialViewFilters?: { [viewId: string]: SelectedFilters };
}

export const ReportProvider = ({ reportId, children, initialViewFilters }: ReportProviderProps) => {
    const { data: report, isLoading, error } = useGetReportQuery(reportId);
    const navigate = useNavigate();
    const location = useLocation();

    const isEditable = report?.hasDraftRevision ?? false;

    const [editMode, setEditMode] = useState(isEditable);

    const [viewFiltersMap, setViewFiltersMap] = useState<{ [viewId: string]: SelectedFilters }>(
        initialViewFilters || {}
    );

    const handleSetViewFilters = useCallback((viewId: string, filters: SelectedFilters | ((prev: SelectedFilters) => SelectedFilters)) => {
        setViewFiltersMap(prevMap => {
            const currentFilters = prevMap[viewId] || defaultInitialFilters;
            const newFilters = typeof filters === 'function' ? filters(currentFilters) : filters;
            return {
                ...prevMap,
                [viewId]: newFilters,
            };
        });
    }, []);

    const handleSetEditMode = useCallback((editMode: boolean) => {
        setEditMode(editMode);
    }, [setEditMode]);

    const value = useMemo(() => ({
        report,
        isLoading,
        error,
        isEditable,
        editMode,
        setEditMode: handleSetEditMode,
        isPublished: false,
        revisionId: null,
        revision: report?.revisions?.[0] ?? null,
        viewFiltersMap,
        setViewFilters: handleSetViewFilters,
    }), [
        report, 
        isLoading, 
        error, 
        editMode, 
        handleSetEditMode, 
        isEditable,
        viewFiltersMap,
        handleSetViewFilters,
    ]);

    // Handle 403 Forbidden errors using reusable guard - check after all hooks
    const forbidden = useForbiddenFallback({
        error,
        onBack: () => navigate(-1),
        message: "You do not have permission to access this report."
    });

    if (error) {
        if ((location?.state?.from as string)?.includes('published')) {
            //redirect to reports list
            navigate('/reports');
            return null;
        }
        
        console.error('Error fetching report',{ error, location});

        // Handle 403 via the guard component
        if (forbidden) {
            return forbidden;
        }

        // Handle 404
        if (hasStatusCode(error) && error.status === 404) {
            return (
                <ErrorCard
                    title="Report Not Found"
                    message="The report you're looking for doesn't exist or may have been deleted."
                    action={() => navigate('/reports')}
                    actionLabel="View All Reports"
                />
            );
        }

        return <div>Error fetching report</div>;
    }

    return <ReportContext.Provider
        value={value}
        key={reportId}
    >
        {children}
    </ReportContext.Provider>;
};

export const useReportContext = () => {
    const context = useContext(ReportContext);
    if (!context) {
        throw new Error('useReport must be used within a ReportProvider');
    }
    return context;
};

